<script lang="ts" module>
  interface getSideNavGroupItemStateProps {
    groupState: NavItemState
    fullId: string
    furthestStepNodeReached: StepNode
    navSteps: LinkedList
  }

  export function getSideNavGroupItemState({
    groupState,
    fullId,
    furthestStepNodeReached,
    navSteps,
  }: getSideNavGroupItemStateProps): NavItemState {
    if (groupState === "disabled" || groupState === "review") return groupState

    const furthestId = furthestStepNodeReached.value.id
    if (furthestId.startsWith(fullId)) return "enabled"

    for (const stepNode of navSteps.forward()) {
      if (stepNode.value.id === furthestId) return "disabled"
      if (stepNode.value.id.startsWith(fullId)) return "enabled"
    }

    return "disabled"
  }
</script>

<script lang="ts">
  import { getNavContext } from "$lib/context"
  import type {
    LinkedList,
    NavGroup,
    NavItem,
    NavItemState,
    StepNode,
  } from "$lib/nav"
  import { _ } from "svelte-i18n"
  import { circle, line, text } from "./nav"

  interface SideNavGroupItemProps {
    navSection: NavItem
    navGroup: NavGroup
    groupState: NavItemState
    isGroupOpen: boolean
  }

  let {
    navSection,
    navGroup,
    groupState,
    isGroupOpen = $bindable(),
  }: SideNavGroupItemProps = $props()

  const nav = getNavContext()

  const fullId = navGroup.id + "." + navSection.id
  const href = navGroup.href + navSection.href

  const sectionState = $derived(
    getSideNavGroupItemState({
      groupState,
      furthestStepNodeReached: nav.furthestStepNodeReached,
      fullId,
      navSteps: nav.navSteps,
    })
  )

  const firstNodeOfSection = $derived(
    [...nav.navSteps.forward()].find((navStep) =>
      navStep.value.href.startsWith(href)
    )
  )
  const active = $derived(nav.currentStepNode.value.id.startsWith(fullId))
  const disabled = $derived("disabled" === sectionState)
</script>

<li role="presentation">
  <a
    role="menuitem"
    class="flex items-center gap-3"
    href={firstNodeOfSection?.value.href}
    class:pointer-events-none={disabled}
    data-sveltekit-preload-data="tap"
    data-testid={`nav-item-${fullId}`}
    aria-disabled={disabled}
    data-nav-state={sectionState}
    tabindex={disabled ? -1 : 0}
    onclick={() => (isGroupOpen = false)}
  >
    <div
      class="relative flex h-6 w-6 items-center justify-center rounded-full"
      role="presentation"
    >
      <!-- line -->
      <div class={line({ size: "small", disabled })}></div>
      <!-- circle -->
      <div class={circle({ size: "small", state: sectionState, active })}></div>
    </div>
    <span class={text({ size: "small", state: sectionState })}>
      {$_(`${fullId}.navLabel`)}
    </span>
  </a>
</li>
